import { useState } from 'react';
import { Modal, TextInput, Textarea, NumberInput, Stack, Group, Button, Alert } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import ImageUpload from '../../components/ImageUpload';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import type { Technician } from './TechniciansPage';

export default function TechnicianModal({
  initial,
  onClose,
  onSaved,
  onDeleted,
}: {
  initial: Technician | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const [name, setName] = useState(initial?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [rate, setRate] = useState<number | string>(initial?.hourlyRateEur ?? 10);
  const [image, setImage] = useState<string | null>(initial?.imagePath ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const dirty =
    name !== (initial?.name ?? '') ||
    notes !== (initial?.notes ?? '') ||
    Number(rate) !== (initial?.hourlyRateEur ?? 10) ||
    image !== (initial?.imagePath ?? null);

  function confirmClose() {
    if (!dirty) return onClose();
    modals.openConfirmModal({
      title: 'Discard changes?',
      children: 'You will lose your edits.',
      labels: { confirm: 'Discard', cancel: 'Keep editing' },
      confirmProps: { color: 'red' },
      onConfirm: onClose,
    });
  }

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    if (Number(rate) < 10) return setErr('Hourly rate must be at least 10 EUR.');
    if ((notes ?? '').length > 500) return setErr('Notes must be ≤ 500 chars.');
    setBusy(true);
    try {
      const body = { name: name.trim(), notes: notes || null, hourlyRateEur: Number(rate), imagePath: image };
      if (initial) {
        await api.put(`/technicians/${initial.id}`, body);
      } else {
        await api.post('/technicians', body);
      }
      notifications.show({ message: 'Technician saved.', color: 'green' });
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!initial) return;
    modals.openConfirmModal({
      title: `Delete ${initial.name}?`,
      children: 'This cannot be undone.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/technicians/${initial.id}`);
          notifications.show({ message: 'Technician deleted.', color: 'gray' });
          onDeleted();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  return (
    <Modal opened onClose={confirmClose} title={initial ? `Edit ${initial.name}` : 'Add Technician'} size="lg">
      <Stack>
        <ImageUpload entity="technicians" value={image} onChange={setImage} />
        <TextInput
          data-tour="technician-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          maxLength={120}
          disabled={!writable}
        />
        <Textarea
          label="Notes"
          value={notes ?? ''}
          onChange={(e) => setNotes(e.currentTarget.value)}
          maxLength={500}
          autosize
          minRows={3}
          description={`${(notes ?? '').length}/500`}
          disabled={!writable}
        />
        <NumberInput
          data-tour="technician-rate"
          label="Hourly Rate (EUR)"
          value={rate}
          onChange={setRate}
          min={10}
          step={0.5}
          decimalScale={2}
          required
          disabled={!writable}
        />
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="space-between" mt="md">
          <div>
            {initial && writable && (
              <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>
            )}
          </div>
          <Group>
            <Button variant="default" onClick={confirmClose}>Close</Button>
            {writable && <Button onClick={save} loading={busy} data-tour="technician-save">Save</Button>}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
